// Central mapping of UI field keys to OnBase keyword names.
// Each array is prioritized left-to-right; the first present keyword value wins.

import { ValidationRule } from "../utils/validation";

export type FieldKey =
  | "prefix"
  | "legalFirstName"
  | "legalMiddleName"
  | "legalLastName"
  | "suffix"
  | "preferredFirstName"
  | "preferredLastName"
  | "primaryEmail"
  | "alternateEmail"
  | "priorUnitsYtd"
  | "priorGciYtd"
  | "priorVolumeYtd"
  | "fromCBFranchiseNonNRT"
  | "priorBroker"
  | "federalTaxClassification"
  | "methodOfIntroduction";

export const ONBASE_TO_UI_FIELD_MAP: Record<FieldKey, string[]> = {
  prefix: ["Salutation"],
  legalFirstName: ["Agent First Name"],
  legalMiddleName: ["Legal Middle Name"],
  legalLastName: ["Agent Last Name"],
  suffix: ["Suffix"],
  preferredFirstName: ["Preferred First Name"],
  preferredLastName: ["Preferred Last Name"],
  primaryEmail: ["Agent Primary Email"],
  alternateEmail: ["Agent Secondary Email"],
  priorUnitsYtd: ["AAA - Prior Units"],
  priorGciYtd: ["Previous GCI"],
  priorVolumeYtd: ["AAA - Prior Volume"],
  fromCBFranchiseNonNRT: ["AAA - Brand Partner Checkbox"],
  priorBroker: ["Former Broker"],
  federalTaxClassification: ["Federal Tax Classification"],
  methodOfIntroduction: ["Method of Introduction"],
};

export const resolveFromSelectors = (
  _src: unknown,
  kwMap: Record<string, string>,
  selectors: string[]
): string | undefined => {
  // Only check for keyword values by name in priority order
  for (const name of selectors) {
    const v = kwMap[name];
    if (v !== undefined && v !== "") return v;
  }
  return undefined;
};

// Form mapper types re-used across forms
export interface FormFieldDef {
  label: string;
  keywordId?: number;
  placeholder?: string;
  displayLabel?: string; // optional override if different from label
  fieldType?: string; // kept generic here; narrowed in component via NormalizedField['type']
  options?: string[];
  validation?: ValidationRule[]; // validation rules for this field
  required?: boolean; // shorthand for required validation
}

export interface FormSectionDef {
  title: string | undefined;
  fields: FormFieldDef[];
}

// Dynamic builder so titles can directly reference metroConfig header values
interface MetroBodySlice { [k: string]: unknown; }
export interface MetroConfigLike { Page2Body?: MetroBodySlice; Page4Body?: MetroBodySlice; Page5Body?: MetroBodySlice; }

export const buildPersonalDemographicFormMapper = (metroConfig: MetroConfigLike | undefined): FormSectionDef[] => [
  {
  title: typeof metroConfig?.Page2Body?.Header2 === 'string' ? metroConfig?.Page2Body?.Header2 : undefined,
    fields: [
      {
        label: "socialsecuritynumber117",
        placeholder: "111-11-1111"
      },
      {
        label: "socialsecuritynumber1001",
        placeholder: "111-11-1111"
      }
    ]
  },
  {
  title: typeof metroConfig?.Page2Body?.Header3 === 'string' ? metroConfig?.Page2Body?.Header3 : undefined,
    fields: [
      {
        label: "checkboxManagement",
        fieldType: "radio",
        options: ["Yes", "No"]
      },
      {
        label: "agentmanagementexperience282",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "agentmanagementindustries284",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "agenteducationlevel283",
        fieldType: "select",
        placeholder: "Select"
      }
    ]
  },
  {
  title: typeof metroConfig?.Page2Body?.Header4 === 'string' ? metroConfig?.Page2Body?.Header4 : undefined,
    fields: [
      {
        label: "languagesspoken117",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "birthdate60",
        fieldType: "date"
      }
    ]
  },
  {
  title: typeof metroConfig?.Page2Body?.Header5 === 'string' ? metroConfig?.Page2Body?.Header5 : undefined,
    fields: [
      {
        label: "gender291",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "ethnicity61",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "maritalstatus142",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "militarystatus264",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "agentdiversitymembership279",
        fieldType: "select",
        placeholder: "Select"
      }
    ]
  },
  {
    title: 'Business Entity',
    fields: [
      {
        label: 'agentdba74',
        fieldType: 'select',
        options: ['Yes', 'No'],
        displayLabel: typeof metroConfig?.Page2Body?.Header6 === 'string' ? metroConfig?.Page2Body?.Header6 : undefined
      },
      {
        label: 'businessname75',
        displayLabel: typeof metroConfig?.Page2Body?.Header7 === 'string' ? metroConfig?.Page2Body?.Header7 : undefined
      },
      {
        label: 'employeridentificationnumber78',
        placeholder: "XXXXXXXXX",
        displayLabel: typeof metroConfig?.Page2Body?.Header8 === 'string' ? metroConfig?.Page2Body?.Header8 : undefined
      },
      {
        label: 'firm126',
        fieldType: 'select',
        placeholder: 'Select',
        displayLabel: typeof metroConfig?.Page2Body?.Header9 === 'string' ? metroConfig?.Page2Body?.Header9 : undefined
      }
    ]
  }
];

export const buildResidentialAddressFormMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => [
  {
    title: "",
    fields: [
      {
        label: "agentaddress1111"
      },
      {
        label: "agentaddress2115"
      },
      {
        label: "agentcity112"
      },
      {
        label: "agentstate120",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "agentzip114"
      },
      {
        label: "homephonenumber33",
        fieldType: "phone"
      },
      {
        label: "cellphonenumber34",
        fieldType: "phone"
      }
    ]
  }
];

// License & Emergency Contact form mapper
export const buildLicenseEmergencyContactFormMapper = (metroConfig: MetroConfigLike | undefined): FormSectionDef[] => [
  {
  title: typeof metroConfig?.Page4Body?.Header2 === 'string' ? metroConfig?.Page4Body?.Header2 : undefined,
    fields: [
      {
        label: "emergencycontactfirstname93"
      },
      {
        label: "emergencycontactlastname94"
      },
      {
        label: "emergencycontactphone95",
        fieldType: "phone"
      },
      {
        label: "emergencycontactrelationship96",
        fieldType: "select",
        placeholder: "Select",
        options: ["Child", "Family", "Friend", "Other", "Parent", "Partner", "Spouse"]
      }
    ]
  },
  {
  title: typeof metroConfig?.Page4Body?.Header3 === 'string' ? metroConfig?.Page4Body?.Header3 : undefined,
    fields: [
      {
        label: "currentrealestatelicense295",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "realestatelicensenumber141"
      },
      {
        label: "realestatelicenseexpirationdate92",
        fieldType: "date"
      },
      {
        label: "licensestate119",
        fieldType: "select",
        placeholder: "Select"
      },
      {
        label: "nrdsid278"
      }
    ]
  }
];

// Universal Cares Donation Enrollment mapper (works for both CB and Corcoran)
export const buildUniversalCaresDonationFormMapper = (metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: typeof metroConfig?.Page5Body?.Header2 === 'string' ? metroConfig?.Page5Body?.Header2 : undefined,
      fields: [
        // CB Cares fields
        {
          label: 'donationpertrx111',
          placeholder: '$ [0.00]'
        },
        {
          label: 'donationcaprequired112',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'donationcap113',
          placeholder: '$ [0.00]',
        },
        // Corcoran Cares fields
        {
          label: 'corcorancontribution2128',
          placeholder: '$ 0'
        },
        {
          label: 'corcorancaprequired2132',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'corcorancap2130',
          placeholder: '$ 0'
        }
      ]
    }
  ];
};

// CB Cares Donation Enrollment mapper (legacy - use UniversalCaresDonationForm instead)
export const buildCBCaresDonationFormMapper = (metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: typeof metroConfig?.Page5Body?.Header2 === 'string' ? metroConfig?.Page5Body?.Header2 : undefined,
      fields: [
        {
          label: 'donationpertrx111',
          placeholder: '$ [0.00]'
        },
        {
          label: 'donationcaprequired112',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'donationcap113',
          placeholder: '$ [0.00]',
        }
      ]
    }
  ];
};

// Corcoran Cares Donation Enrollment mapper (legacy - use UniversalCaresDonationForm instead)
export const buildCorcoranCaresDonationFormMapper = (metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: typeof metroConfig?.Page5Body?.Header2 === 'string' ? metroConfig?.Page5Body?.Header2 : undefined,
      fields: [
        {
          label: 'corcorancontribution2128',
          placeholder: '$ 0'
        },
        {
          label: 'corcorancaprequired2132',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'corcorancap2130',
          placeholder: '$ 0'
        }
      ]
    }
  ];
};

// Individual resident license application - Criminal history mapper
export const buildIndividualResidentLicenseApplicationCriminalHistoryTestificationMapper = (__metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q1metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Individual resident license application - License / registration issues mapper
export const buildIndividualResidentLicenseApplicationLicenseRegistrationIssuesMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q2metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Individual resident license application - Judgments / bankruptcy mapper
export const buildIndividualResidentLicenseApplicationJudgmentsBankruptcyMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q3metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Individual resident license application - Delinquent taxes mapper
export const buildIndividualResidentLicenseApplicationDelinquentTaxesMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q4metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'q4ametro619299',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Individual resident license application - Lawsuits / arbitration mapper
export const buildIndividualResidentLicenseApplicationLawsuitsArbitrationMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q5metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Individual resident license application - Child support obligations mapper
export const buildIndividualResidentLicenseApplicationChildSupportObligationsMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q6metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'q6ametro619240',
          placeholder: 'Enter months (numeric)'
        },
        {
          label: 'q6bmetro619294',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'q6cmetro619295',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Individual resident license application - Licensing in other states mapper
export const buildIndividualResidentLicenseApplicationLicensingOtherStatesMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q7metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Individual resident license application - Company ownership mapper
export const buildIndividualResidentLicenseApplicationCompanyOwnershipMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q8metro619240',
          fieldType: 'select',
          placeholder: 'Select'
        },
        {
          label: 'priorrealestatecompanyname245',
        },
        {
          label: 'priorrealestatelicensenumber246',
        },
      ]
    }
  ];
};

// Individual resident license application - Unclaimed property mapper
export const buildIndividualResidentLicenseApplicationUnclaimedPropertyMapper = (_metroConfig: MetroConfigLike | undefined): FormSectionDef[] => {
  return [
    {
      title: undefined,
      fields: [
        {
          label: 'q9metro619242',
          fieldType: 'select',
          placeholder: 'Select'
        }
      ]
    }
  ];
};

// Backwards-compatible export expected by callers
export const buildIndividualResidentLicenseApplicationCriminalHistoryMapper =
  buildIndividualResidentLicenseApplicationCriminalHistoryTestificationMapper;